import { Dimensions } from 'react-native';
// Step state machine for the guided onboarding. Driven by ONBOARDING_STEPS and
// the tourBus; persists { stepId, lineIdx, doneAt, skippedAt } under `v10onboarding`.
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { View } from 'react-native';
import { readNav, writeNav } from '../../lib/navPersist';
import { crossAlert } from '../../lib/crossAlert';
import { ONBOARDING_STEPS } from './onboardingScript';
import { registerTarget } from './targetRegistry';
import { tourBus } from './tourBus';
import { measureTarget } from './targetRegistry';
import type { TourLine, TourStep } from './types';

const NAV_KEY = 'v10onboarding';
const RESTART_EVENT = 'onboarding:restart';
const MUTE_KEY = 'v10onboardingMute';

interface Persisted {
  stepId: string;
  lineIdx: number;
  doneAt: number | null;
  skippedAt: number | null;
}

const freshRecord = (): Persisted => ({ stepId: ONBOARDING_STEPS[0].id, lineIdx: 0, doneAt: null, skippedAt: null });

/** Callable from anywhere (e.g. Settings, outside the provider): resets the saved
 *  record and tells a mounted provider to start over. */
export function restartOnboarding(): void {
  writeNav(NAV_KEY, freshRecord());
  tourBus.emit(RESTART_EVENT);
}

export interface OnboardingValue {
  active: boolean;
  step: TourStep | null;
  stepIdx: number;
  line: TourLine | null;
  lineIdx: number;
  /** The last line of the current step has been shown, so its target is live. */
  linesDone: boolean;
  /** The id of the control that is currently live (only once linesDone). */
  liveTarget: string | null;
  /** The id of the control the step points at (even while its lines are playing). */
  stepTarget: string | null;
  advance: () => void;
  nextLine: () => void;
  replayLine: () => void;
  /** Bumps on every replayLine() so audio/caption effects can re-run. */
  replayTick: number;
  /** The user has tapped Begin (the audio-unlock gesture) since onboarding last activated. */
  begun: boolean;
  begin: () => void;
  /** Captions only: no audio, lines auto-advance after a reading delay. Persisted. */
  muted: boolean;
  toggleMute: () => void;
  skip: () => void;
  restart: () => void;
}

const NOOP_VALUE: OnboardingValue = {
  active: false, step: null, stepIdx: 0, line: null, lineIdx: 0, linesDone: false, liveTarget: null, stepTarget: null,
  advance: () => {}, nextLine: () => {}, replayLine: () => {}, replayTick: 0, begun: false, begin: () => {}, muted: false, toggleMute: () => {}, skip: () => {}, restart: () => {},
};

const Ctx = createContext<OnboardingValue>(NOOP_VALUE);

export function useOnboarding(): OnboardingValue {
  return useContext(Ctx);
}

/** Register a control as a tour target. Attach `onLayoutRef` as the view's `ref`. */
export function useTourTarget(id: string | undefined): { unlocked: boolean; highlighted: boolean; onLayoutRef: (node: View | null) => void } {
  const { active, stepTarget, linesDone } = useContext(Ctx);
  const unregister = useRef<(() => void) | null>(null);
  const nodeRef = useRef<View | null>(null);
  const onLayoutRef = useCallback(
    (node: View | null) => {
      unregister.current?.();
      unregister.current = null;
      if (node && id) unregister.current = registerTarget(id, node);
      nodeRef.current = node;
    },
    [id],
  );
  useEffect(() => () => {
    unregister.current?.();
    unregister.current = null;
  }, []);
  const highlighted = !!id && active && stepTarget === id;
  // Web: a target below the fold can't be reached (the lock blocks scrolling), so bring it into view.
  useEffect(() => {
    if (!highlighted) return;
    const el = nodeRef.current as unknown as { scrollIntoView?: (o: object) => void } | null;
    const tm = setTimeout(() => el?.scrollIntoView?.({ block: 'center', behavior: 'smooth' }), 60);
    return () => clearTimeout(tm);
  }, [highlighted]);
  return { unlocked: highlighted && linesDone, highlighted, onLayoutRef };
}

export type TourScreen = TourStep['screen'];

export function OnboardingProvider({ children, eligible = false, navigate, getScreen }: {
  children: React.ReactNode;
  /** True only when the account has no existing setup (and its data has finished loading). Gates first-run auto-start. */
  eligible?: boolean;
  /** Bring `screen` on screen. The host decides whether anything needs to move. */
  navigate?: (screen: TourScreen) => void;
  /** The canvas screen currently on show, so a "go to X" wait is already satisfied when we're there. */
  getScreen?: () => string;
}) {
  const [active, setActive] = useState(false);
  const [waitPos, setWaitPos] = useState(0);
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;
  const eligibleRef = useRef(eligible);
  eligibleRef.current = eligible;
  const [loaded, setLoaded] = useState(false);
  const [hasRecord, setHasRecord] = useState(false);
  const [stepIdx, setStepIdx] = useState(0);
  const [lineIdx, setLineIdx] = useState(0);
  const [replayTick, setReplayTick] = useState(0);
  const [begun, setBegun] = useState(false);
  const [muted, setMuted] = useState(false);
  useEffect(() => {
    void readNav<boolean>(MUTE_KEY).then((m) => setMuted(m === true));
  }, []);
  const toggleMute = useCallback(() => {
    setMuted((m) => {
      writeNav(MUTE_KEY, !m);
      return !m;
    });
  }, []);
  const begin = useCallback(() => setBegun(true), []);

  // Mirrors for the bus listener (which must not re-subscribe on every render).
  const activeRef = useRef(false);
  const stepIdxRef = useRef(0);
  const lineIdxRef = useRef(0);
  const waitIdx = useRef(0);
  const record = useRef<Persisted>(freshRecord());

  const apply = useCallback((idx: number, line: number, isActive: boolean) => {
    stepIdxRef.current = idx;
    lineIdxRef.current = line;
    activeRef.current = isActive;
    // Resuming mid-step (line > 0) on a multi-target step: its earlier targets were already
    // narrated, so land on the last one (e.g. "Now tap Mountain") instead of a stale first spotlight.
    const wf = ONBOARDING_STEPS[idx]?.waitFor;
    const pos = line > 0 && Array.isArray(wf) ? wf.length - 1 : 0;
    waitIdx.current = pos;
    setWaitPos(pos);
    setStepIdx(idx);
    setLineIdx(line);
    setActive(isActive);
    if (!isActive) setBegun(false);
  }, []);

  const persist = useCallback((patch: Partial<Persisted>) => {
    record.current = {
      ...record.current,
      stepId: ONBOARDING_STEPS[stepIdxRef.current]?.id ?? record.current.stepId,
      lineIdx: lineIdxRef.current,
      ...patch,
    };
    writeNav(NAV_KEY, record.current);
  }, []);

  const start = useCallback(() => {
    record.current = freshRecord();
    setBegun(false);
    apply(0, 0, true);
    persist({});
  }, [apply, persist]);

  // Resume (or first-run start) on mount.
  useEffect(() => {
    let alive = true;
    void readNav<Persisted>(NAV_KEY).then((saved) => {
      if (!alive) return;
      setLoaded(true);
      if (!saved) return; // first run: the eligibility effect below decides whether to start
      setHasRecord(true);
      record.current = { stepId: saved.stepId, lineIdx: saved.lineIdx ?? 0, doneAt: saved.doneAt ?? null, skippedAt: saved.skippedAt ?? null };
      if (saved.doneAt || saved.skippedAt) return;
      let idx = ONBOARDING_STEPS.findIndex((s) => s.id === saved.stepId);
      if (idx < 0) {
        // The step was renamed or split since this was saved: resume at the start of the same stop,
        // never back at the very beginning.
        const stop = /^s(\d)_/.exec(saved.stepId ?? '')?.[1];
        idx = stop ? ONBOARDING_STEPS.findIndex((s) => s.stop === Number(stop) && /_handoff$/.test(s.id)) : -1;
        if (idx < 0) {
          start();
          return;
        }
        apply(idx, 0, true);
        return;
      }
      const maxLine = ONBOARDING_STEPS[idx].lines.length - 1;
      apply(idx, Math.max(0, Math.min(saved.lineIdx ?? 0, maxLine)), true);
    });
    return () => {
      alive = false;
    };
  }, [apply, start]);

  // First-run gate: no saved record AND no existing setup on the account.
  useEffect(() => {
    if (loaded && !hasRecord && eligible) {
      setHasRecord(true);
      start();
    }
  }, [loaded, hasRecord, eligible, start]);

  const advance = useCallback(() => {
    if (!activeRef.current) return;
    const next = stepIdxRef.current + 1;
    if (next >= ONBOARDING_STEPS.length) {
      apply(stepIdxRef.current, lineIdxRef.current, false);
      persist({ doneAt: Date.now() });
      return;
    }
    apply(next, 0, true);
    persist({});
  }, [apply, persist]);

  const nextLine = useCallback(() => {
    if (!activeRef.current) return;
    const step = ONBOARDING_STEPS[stepIdxRef.current];
    if (lineIdxRef.current < step.lines.length - 1) {
      lineIdxRef.current += 1;
      setLineIdx(lineIdxRef.current);
      persist({});
    } else if (!step.waitFor) {
      advance();
    }
  }, [advance, persist]);

  const replayLine = useCallback(() => setReplayTick((n) => n + 1), []);

  const skip = useCallback(() => {
    crossAlert('Skip setup?', 'You can replay it any time from Settings.', [
      { text: 'Keep going', style: 'cancel' },
      {
        text: 'Skip setup',
        style: 'destructive',
        onPress: () => {
          apply(stepIdxRef.current, lineIdxRef.current, false);
          persist({ skippedAt: Date.now() });
        },
      },
    ]);
  }, [apply, persist]);

  // Bus: step completion + external restart.
  useEffect(() => {
    return tourBus.on((event) => {
      if (event === RESTART_EVENT) {
        start();
        return;
      }
      if (!activeRef.current) return;
      const step = ONBOARDING_STEPS[stepIdxRef.current];
      if (!step?.waitFor) return;
      // Controls are locked until the last line has been shown; ignore early events.
      if (lineIdxRef.current < step.lines.length - 1) return;
      if (Array.isArray(step.waitFor)) {
        if (event !== step.waitFor[waitIdx.current]) return;
        waitIdx.current += 1;
        if (waitIdx.current < step.waitFor.length) {
          setWaitPos(waitIdx.current); // spotlight moves to the next target
          return;
        }
      } else if (event !== step.waitFor) {
        return;
      }
      advance();
    });
  }, [advance, start]);

  const step = active ? ONBOARDING_STEPS[stepIdx] ?? null : null;
  // Resume / step change: make sure the step's screen is on screen so its target exists.
  useEffect(() => {
    if (active && step) navigateRef.current?.(step.screen);
  }, [active, step]);
  const currentTarget = step ? step.targets?.[waitPos] ?? step.target ?? null : null;
  const linesDone = !!step && lineIdx >= step.lines.length - 1;
  const rewound = useRef(new Set<string>());
  const getScreenRef = useRef(getScreen);
  getScreenRef.current = getScreen;
  // A step that lives inside a deity's tool can't run if the tool isn't open (reload, or the user
  // left it). Rewind to that stop's handoff (tap the deity, then open the tool).
  useEffect(() => {
    if (!active || !step || step.screen !== 'deitytool' || /_return$/.test(step.id)) return;
    const tm = setTimeout(() => {
      if (!activeRef.current || getScreenRef.current?.() === 'deitytool') return;
      const introIdx = ONBOARDING_STEPS.findIndex((st) => st.stop === step.stop && /_handoff$/.test(st.id));
      if (introIdx >= 0 && introIdx !== stepIdxRef.current) {
        apply(introIdx, 0, true);
        persist({});
      }
    }, 1200);
    return () => clearTimeout(tm);
  }, [active, step, apply, persist]);
  // The lit control must exist. If it hasn't appeared after a few seconds (stale resume, the user
  // closed a view another way), rewind to this stop's handoff so the path rebuilds itself.
  const liveId = step && linesDone ? step.targets?.[waitPos] ?? step.target ?? null : null;
  useEffect(() => {
    if (!active || !step || !liveId || step.stop === 0 || /_handoff$/.test(step.id)) return;
    let gone = 0;
    const iv = setInterval(async () => {
      const { width: ww, height: wh } = Dimensions.get('window');
      const r = await measureTarget(liveId, ww, wh);
      gone = r && r.w > 0 && r.y >= 0 ? 0 : gone + 1;
      if (gone < 4) return;
      clearInterval(iv);
      if (!activeRef.current) return;
      // Rewind once per step; if the control still can't be found, skip the step rather than trap the user.
      if (rewound.current.has(step.id)) { advance(); return; }
      rewound.current.add(step.id);
      const h = ONBOARDING_STEPS.findIndex((st) => st.stop === step.stop && /_handoff$/.test(st.id));
      if (h >= 0) { apply(h, 0, true); persist({}); }
    }, 800);
    return () => clearInterval(iv);
  }, [active, step, liveId, apply, persist, advance]);
  // A wait for `nav:<screen>` is already met if that screen is showing (e.g. resumed after a reload,
  // or the user left a tool another way). Without this the step waits on a control that isn't there.
  useEffect(() => {
    if (!step || !linesDone) return;
    const wants = Array.isArray(step.waitFor) ? step.waitFor[waitPos] : step.waitFor;
    const m = typeof wants === 'string' ? /^nav:(\w+)$/.exec(wants) : null;
    if (!m) return;
    const tm = setTimeout(() => {
      if (getScreenRef.current?.() === m[1]) tourBus.emit(wants as string);
    }, 400);
    return () => clearTimeout(tm);
  }, [step, linesDone, waitPos]);
  const value = useMemo<OnboardingValue>(
    () => ({
      active,
      step,
      stepIdx,
      line: step?.lines[Math.min(lineIdx, step.lines.length - 1)] ?? null,
      lineIdx,
      linesDone,
      liveTarget: linesDone ? currentTarget : null,
      stepTarget: currentTarget,
      advance,
      nextLine,
      replayLine,
      replayTick,
      begun,
      begin,
      muted,
      toggleMute,
      skip,
      restart: start,
    }),
    [active, step, stepIdx, lineIdx, linesDone, currentTarget, advance, nextLine, replayLine, replayTick, begun, begin, muted, toggleMute, skip, start],
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}
